import {
  customValidator,
  ErrorMessage,
  IsBoolean,
  IsNotEmpty,
  IsOptional,
  IsString,
  ValidateNested,
} from '@pristine-ts/class-validator';
import { translationBaseKey } from '../../../decorators/translation-base-key.decorator';
import { CalculationRunOptions } from '../calculation-run.options';
import {
  advancedFormControl,
  advancedFormArray,
} from '@magieno/common';
import { CalculationKeynameEnum } from '../../../enums/calculations/calculation-keyname.enum';
import { FieldTypeEnum } from '../../../enums/field-type.enum';
import { FormControlTypeEnum } from '@magieno/common';
import { RolloverPropertyTransferredOptions } from './rollover-property-transferred.options';
import { RolloverSharesClassOptions } from './rollover-shares-class.options';
import { RolloverShareholderOptions } from './rollover-shareholder.options';
import { RolloverNonShareConsiderationOptions } from './rollover-non-share-consideration.options';
import { ErrorMessageBuilder } from '../../../builders/error-message.builder';
import { CalculationDate } from '../../../models/calculation.date';
import { type } from '@pristine-ts/data-mapping-common';

@translationBaseKey('rollover-options')
export class RolloverOptions extends CalculationRunOptions {
  @advancedFormControl({
    type: FormControlTypeEnum.Hidden,
  })
  calculationKeyname: CalculationKeynameEnum = CalculationKeynameEnum.Rollover;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nom de la société cessionnaire',
        en: 'Name of the transferee corporation',
      },
      tooltip: {
        fr: 'Indiquez le nom de la société à qui les biens seront transférés dans le cadre de la transaction de roulement.',
        en: 'Specify the name of the corporation to whom the assets will be transferred in the rollover transaction.',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  buyerCorporation: string;

  @type(() => new CalculationDate())
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de signature',
        en: 'Signature date',
      },
      tooltip: {
        fr: 'Indiquez la date à laquelle le contrat de roulement a été signé.',
        en: 'Specify the date when the rollover agreement was signed.',
      },
      description: {
        fr: 'Pour la `Date de signature` sur l\'accord de roulement, la meilleure pratique consiste à utiliser la **date à laquelle l\'accord est finalisé et signé par vous et un dirigeant autorisé de la société.** Idéalement, cette date devrait précéder ou coïncider avec la date d\'entrée en vigueur du transfert d\'actifs.',
        en: 'For the Signature Date on the rollover agreement, the best practice is to use the date the agreement is finalized and signed by both you and an authorized officer of the corporation. This date should ideally precede or coincide with the effective date of the asset transfer.'
      }
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @ValidateNested()
  @IsOptional()
  signatureDate?: CalculationDate;

  @type(() => new CalculationDate())
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date limite de production',
        en: 'Production deadline',
      },
      tooltip: {
        fr: 'Indiquez la date limite de production des formulaires de roulement T2057 et TP-518.',
        en: 'Specify the deadline for filing the T2057 and TP-518 rollover forms.',
      },
      description: {
        fr: `La \`Date de production\` correspond à la date limite à laquelle les formulaires T2057 et TP-518 dûment remplis et signés doivent être soumis aux autorités fiscales respectives. Il s'agit d'une échéance stricte, et son non-respect peut entraîner des conséquences fiscales négatives importantes.

La règle pour la date limite de production est la **première des dates d'échéance de production des déclarations de revenus, que ce soit celle du cédant (vous) ou du cessionnaire (la société), pour l'année d'imposition au cours de laquelle le transfert a eu lieu.**

Voici comment déterminer votre \`Date de production\` :

-   **Votre date limite de production de déclaration de revenus personnelle :** Si vous êtes un particulier, votre date limite de production est généralement le 30 avril de l'année suivant l'année d'imposition du transfert. Si vous ou votre conjoint(e) avez un revenu de travail indépendant, la date limite est le 15 juin, mais tout impôt dû doit quand même être payé au plus tard le 30 avril.

-   **La date limite de production de la déclaration de revenus de la société :** La déclaration de revenus d'une société doit être produite dans les six mois suivant la fin de son exercice financier.

**Exemple :**

Supposons que vous, en tant que particulier, transférez des actifs à votre entreprise nouvellement constituée en société le 15 octobre 2024. Votre date limite de production pour votre déclaration de revenus de 2024 est le 30 avril 2025. La première fin d'exercice de la société est le 31 décembre 2024, ce qui fixe sa date limite de production au 30 juin 2025.

Dans ce scénario, la **première** des dates limites de production est la vôtre : **le 30 avril 2025**. Par conséquent, votre \`Date de production\` pour la production des formulaires T2057 et TP-518 est le 30 avril 2025.`,
        en: `The Production Deadline refers to the final date by which the completed and signed T2057 and TP-518 forms must be filed with the respective tax authorities. This is a hard deadline, and missing it can have significant negative tax consequences.

The rule for the filing deadline is the **earliest of the income tax return filing deadlines for either the transferor (you) or the transferee (the corporation) for the taxation year in which the transfer occurred.**

Here's how to determine your Production Deadline:

-   **Your Personal Tax Filing Deadline:** If you are an individual, your tax filing deadline is typically April 30th of the year following the tax year of the transfer. If you or your spouse have self-employment income, the deadline is June 15th, but any taxes owing are still due by April 30th.

-   **The Corporation's Tax Filing Deadline:** A corporation's tax return is due six months after its fiscal year-end.

**Example:**

Let's say you, as an individual, transfer assets to your newly incorporated business on October 15, 2024. Your personal tax filing deadline for your 2024 tax year is April 30, 2025. The corporation's first fiscal year-end is December 31, 2024, making its tax filing deadline June 30, 2025.

In this scenario, the **earliest** filing deadline is your personal one: **April 30, 2025**. Therefore, your \`productionDeadline\` for filing the T2057 and TP-518 is April 30, 2025.`
      }
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
      fr: 'Date limite de production',
      en: 'Production deadline',
    },
    tooltip: {
      fr: 'Indiquez la date limite de production des formulaires de roulement T2057 et TP-518.',
      en: 'Specify the deadline for filing the T2057 and TP-518 rollover forms.',
    },
    },
    type: FormControlTypeEnum.DatePicker,
    fieldType: FieldTypeEnum.CalculationDate,
  })
  @ValidateNested()
  @IsOptional()
  productionDeadline?: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Voulez-vous remplir les formulaires de roulement T2057 et TP-518?',
        en: 'Do you want to fill out T2057 and TP-518 rollover forms?',
      },
      tooltip: {
        fr: 'Indiquez si vous souhaitez remplir les formulaires de roulement T2057 et TP-518 pour cette transaction.',
        en: 'Specify if you want to fill out the T2057 and TP-518 rollover forms for this transaction.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  isThereRolloverForm: boolean = false;

  @advancedFormArray({
    emptyNumberOfChildrenFormToCreate: 1,
    elementClassType: RolloverSharesClassOptions,
  })
  @ValidateNested()
  sharesClasses: RolloverSharesClassOptions[] = [];

  @advancedFormArray({
    emptyNumberOfChildrenFormToCreate: 1,
    elementClassType: RolloverPropertyTransferredOptions,
  })
  @ValidateNested()
  propertiesTransferred: RolloverPropertyTransferredOptions[] = [];

  @advancedFormArray({
    emptyNumberOfChildrenFormToCreate: 1,
    elementClassType: RolloverShareholderOptions,
  })
  @ValidateNested()
  @customValidator(async (value: RolloverShareholderOptions[]) => {
    if (value.filter(item => item.isSeller).length !== 1) {
      return {
        message: 'Il doit y avoir exactement un seul actionnaire qui est vendeur.',
        keyname: 'EXACTLY_ONE_SELLER',
      } as ErrorMessage;
    }
    return null;
  })
  shareholders: RolloverShareholderOptions[] = [];

  @advancedFormArray({
    emptyNumberOfChildrenFormToCreate: 0,
    elementClassType: RolloverNonShareConsiderationOptions,
  })
  @ValidateNested()
  nonShareConsiderations: RolloverNonShareConsiderationOptions[] = [];
}
